import type { OnboardingNavigationState } from '@/pages/onboarding/onboarding-navigation-state';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { motion, useReducedMotion } from 'motion/react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { LabeledInput } from '@/components/labeled-input';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';
import { usePatchTenantCreationRequest } from '@/hooks/use-patch-tenant-request';
import {
  OnboardingPreviewProvider,
  useOnboardingPreview,
  useSetOnboardingPreview,
} from '@/pages/onboarding/onboarding-preview-context';
import { IPhoneMockup } from '@/pages/onboarding/mockup/iphone-mockup';
import { AppOpenScene } from '@/pages/onboarding/mockup/scenes/app-open-scene';
import {
  NotificationBanner,
  NOTIFICATION_PREVIEW_TITLE_MAX_LENGTH,
  NOTIFICATION_PREVIEW_BODY_MAX_LENGTH,
  SAMPLE_NOTIFICATION_TITLE,
  SAMPLE_NOTIFICATION_BODY,
} from '@/pages/onboarding/mockup/notification-banner';
import {
  OnboardingPageLayout,
  OnboardingPageHeader,
  OnboardingPageContent,
  OnboardingPageFooter,
} from '@/pages/onboarding/onboarding-page-layout';
import { OnboardingStepsIndicator } from '@/pages/onboarding/onboarding-steps-indicator';
import type { MockupFocus } from '@/pages/onboarding/mobile-studio-geometry';

const DEFAULT_ACCENT_COLOR = '#FF6B00';

// This page's phone has to *start* at the exact transform TabBarConfigPage
// rests on (`transform-origin: 50% 92%`, `scale(1.8)`, `translateY(-42%)` —
// its TAB_BAR_ZOOM_ORIGIN_Y/TAB_BAR_ZOOM_SCALE/TAB_BAR_ZOOM_TRANSLATE_Y),
// not its own fresh one, or there's a hard cut the instant the route
// changes: the previous page's phone visually "ends" in one place and this
// one's first paint starts somewhere else, which reads as the phone
// teleporting before our own slide-down even begins, with only part of the
// move actually animating. So the origin here stays pinned on the tab bar
// (where TabBarConfigPage left it) instead of moving to the notification
// banner's own spot near the top of the screen — only `y` animates, from
// that resting value to a *new* target that still correctly brings the
// banner (~8% down) to center despite the origin being nowhere near it.
//
// `transform-origin` only cancels out of the "bring this point to center"
// math in the special case where it's pinned exactly on the point being
// centered (TabBarConfigPage's own case, where its formula simplifies to
// `50 - origin`). Anywhere else the general form is
// `50 - focal*scale + origin*(scale-1)` (in percent), which is what's used
// below for the notification banner's target.
const SHARED_ZOOM_ORIGIN_Y = 92; // must match TabBarConfigPage's TAB_BAR_ZOOM_ORIGIN_Y
const SHARED_ZOOM_ORIGIN = `50% ${SHARED_ZOOM_ORIGIN_Y}%`;
const SHARED_ZOOM_SCALE = 1.8; // must match TabBarConfigPage's TAB_BAR_ZOOM_SCALE

// Below lg the mockup's own SHARED_ZOOM_SCALE zoom stays (it is not a
// ZoomedIPhoneMockup) and already centers the tab bar / notification: the
// Studio only sizes it so the zoomed screen is about as wide as the canvas.
const MOBILE_FOCUS: MockupFocus = {
  x: 0.5,
  y: 0.5,
  zoom: 1 / SHARED_ZOOM_SCALE,
};

// TabBarConfigPage's own resting value (`50 - origin`, the special case
// above, since its origin sits on its own focal point) — this page's
// starting `y`, for continuity.
const TAB_BAR_RESTING_TRANSLATE_Y = `${50 - SHARED_ZOOM_ORIGIN_Y}%`;

// Where the notification banner sits, ~8% down the frame (NotificationBanner's `top-3`).
const NOTIFICATION_FOCAL_Y = 8;
const NOTIFICATION_ZOOM_TRANSLATE_Y = `${
  50 -
  NOTIFICATION_FOCAL_Y * SHARED_ZOOM_SCALE +
  SHARED_ZOOM_ORIGIN_Y * (SHARED_ZOOM_SCALE - 1)
}%`;

const CENTER_IN_DELAY_MS = 400;
const CENTER_TRANSITION_DURATION_MS = 700;

export default function NotificationPreviewPage() {
  return (
    <OnboardingPreviewProvider>
      <NotificationPreviewPageContent />
    </OnboardingPreviewProvider>
  );
}

function NotificationPreviewPageContent() {
  const navigate = useNavigate();
  const patchCreationRequest = usePatchTenantCreationRequest();
  const preview = useOnboardingPreview();
  const setPreview = useSetOnboardingPreview();
  const shouldReduceMotion = useReducedMotion();

  const [title, setTitle] = useState(preview.notificationPreviewTitle ?? '');
  const [body, setBody] = useState(preview.notificationPreviewBody ?? '');

  // Frame starts exactly at TabBarConfigPage's resting position (see
  // TAB_BAR_RESTING_TRANSLATE_Y) for continuity across the navigation, then
  // slides shortly after mount to bring the notification spot (near the top
  // of the screen) to the column's center. No zoom-out/zoom-in, just a
  // translate — scale never changes from SHARED_ZOOM_SCALE.
  const [isCentered, setIsCentered] = useState(false);
  // The notification banner is always rendered (see its own doc comment for
  // why — StrictMode swallows mount-triggered entrances) and only slides
  // into view once the camera slide above has fully settled — centering the
  // focal point comes first, the notification "surging" in is a separate,
  // later beat, not something that races the camera move.
  const [showNotification, setShowNotification] = useState(false);
  // "Voltar" plays this same slide in reverse (back to TabBarConfigPage's
  // resting position) before navigating away, instead of cutting straight
  // to the tab-bar page — same rationale as TabBarConfigPage's own
  // `isLeaving`/onBack: the outgoing frame has to already be sitting where
  // the destination page's entrance expects it, or arriving there replays
  // the exact same teleport-then-slide bug this page's own entrance had.
  const [isLeaving, setIsLeaving] = useState(false);

  useEffect(() => {
    if (shouldReduceMotion) {
      setIsCentered(true);
      setShowNotification(true);
      return;
    }
    const centerTimeout = setTimeout(
      () => setIsCentered(true),
      CENTER_IN_DELAY_MS
    );
    const notificationTimeout = setTimeout(
      () => setShowNotification(true),
      CENTER_IN_DELAY_MS + CENTER_TRANSITION_DURATION_MS
    );
    return () => {
      clearTimeout(centerTimeout);
      clearTimeout(notificationTimeout);
    };
  }, [shouldReduceMotion]);

  // Optional test notification — empty fields show NotificationBanner's own
  // sample placeholder, and text beyond the character limit cannot be typed
  // (input is hard-capped at the same limit the preview truncates at).
  useEffect(() => {
    const timeout = setTimeout(() => {
      setPreview({
        notificationPreviewTitle: title || null,
        notificationPreviewBody: body || null,
      });
    }, 300);
    return () => clearTimeout(timeout);
  }, [title, body, setPreview]);

  const onContinue = async () => {
    await patchCreationRequest.mutateAsync({
      notificationPreviewTitle: title,
      notificationPreviewBody: body,
    });
    navigate('../complete', { viewTransition: true });
  };

  const onBack = () => {
    if (isLeaving) {
      return;
    }
    setIsLeaving(true);
    setShowNotification(false);
    setIsCentered(false);
    const delay = shouldReduceMotion ? 0 : CENTER_TRANSITION_DURATION_MS;
    setTimeout(() => {
      // `cameFromNotificationBack` tells TabBarConfigPage to mount without
      // its icon-morph tag — see its own doc comment, this page's
      // AppOpenScene never carries that tag, so leaving it on there would
      // pair against nothing and race our slide-down animation with the
      // browser's own view-transition one.
      navigate('../tab-bar', {
        viewTransition: true,
        state: {
          cameFromNotificationBack: true,
        } satisfies OnboardingNavigationState,
      });
    }, delay);
  };

  return (
    <OnboardingPageLayout
      mobileFocus={MOBILE_FOCUS}
      // Fits the short form only between the page transitions: at the
      // tab-bar step's height while arriving and leaving, so the Studio
      // matches there.
      mobileFitSheet={isCentered}
      mockup={
        <motion.div
          style={{ transformOrigin: SHARED_ZOOM_ORIGIN }}
          // `scale` is identical in both states — only `y` actually moves —
          // but it still has to be animated (not left as a static `style`
          // value) alongside `y`: Motion composes x/y/scale/rotate into a
          // single `transform` from whichever of initial/animate/style last
          // touched each one, and mixing a static `style.scale` with an
          // animated `y` was found to fight over that transform instead of
          // combining, producing a jump instead of a smooth slide. Keeping
          // both in the same initial/animate pair (ZoomedIPhoneMockup does
          // the same) avoids that.
          initial={
            shouldReduceMotion
              ? false
              : { scale: SHARED_ZOOM_SCALE, y: TAB_BAR_RESTING_TRANSLATE_Y }
          }
          animate={{
            scale: SHARED_ZOOM_SCALE,
            y: isCentered
              ? NOTIFICATION_ZOOM_TRANSLATE_Y
              : TAB_BAR_RESTING_TRANSLATE_Y,
          }}
          transition={{
            duration: CENTER_TRANSITION_DURATION_MS / 1000,
            ease: [0.16, 1, 0.3, 1],
          }}
        >
          <IPhoneMockup sceneKey="app-open">
            <div className="relative h-full w-full">
              <AppOpenScene
                tabs={preview.tabs ?? []}
                accentColor={preview.accentColor ?? DEFAULT_ACCENT_COLOR}
              />
              <NotificationBanner
                appIconUrl={preview.appIconUrl}
                title={title}
                body={body}
                visible={showNotification}
              />
            </div>
          </IPhoneMockup>
        </motion.div>
      }
    >
      <OnboardingStepsIndicator currentStep="notification" />

      <OnboardingPageHeader
        title="Teste uma notificação"
        subtitle="Veja como uma notificação vai aparecer com a marca do seu app (opcional)."
      />

      <OnboardingPageContent>
        {/* Below lg the form sits in the Studio's sheet, already a surface of
            its own: no card chrome, and no card title repeating the page
            title, so both fields fit above the fold. */}
        <Card className="max-lg:rounded-none max-lg:border-0 max-lg:bg-transparent max-lg:shadow-none">
          <CardHeader className="max-lg:hidden">
            <CardTitle>Notificação de teste</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 max-lg:p-0">
            <LabeledInput label="Título" labelFor="notificationTitle">
              <Input
                id="notificationTitle"
                value={title}
                placeholder={SAMPLE_NOTIFICATION_TITLE}
                maxLength={NOTIFICATION_PREVIEW_TITLE_MAX_LENGTH}
                onChange={(e) => setTitle(e.target.value)}
              />
            </LabeledInput>

            <LabeledInput label="Mensagem" labelFor="notificationBody">
              <Input
                id="notificationBody"
                value={body}
                placeholder={SAMPLE_NOTIFICATION_BODY}
                maxLength={NOTIFICATION_PREVIEW_BODY_MAX_LENGTH}
                onChange={(e) => setBody(e.target.value)}
              />
            </LabeledInput>
          </CardContent>
        </Card>
      </OnboardingPageContent>

      <OnboardingPageFooter className="justify-between">
        <Button
          type="button"
          variant="outline"
          disabled={patchCreationRequest.isPending || isLeaving}
          onClick={onBack}
        >
          Voltar
        </Button>
        <Button
          type="button"
          disabled={patchCreationRequest.isPending || isLeaving}
          className="min-w-[100px]"
          onClick={onContinue}
        >
          {patchCreationRequest.isPending ? (
            <Loader2 className="animate-spin" />
          ) : (
            'Continuar'
          )}
        </Button>
      </OnboardingPageFooter>
    </OnboardingPageLayout>
  );
}
